// T1-A MEO 入稿フォーム セクション群 (2026-05-04 PR 4).
// Spec: design/seo-landmark-spec.md §1.3 / §5.1
//
// LandmarkFormSheet.tsx から呼ばれる小コンポーネント集約。
// 親 form (react-hook-form の UseFormReturn) を props で受けて FormField を組む。
// useFieldArray が必要な MenuItemsSection / ImagesSection は parent で生成した
// fieldArray を props で受け取る (= form インスタンス共有)。

import { Button } from '@/components/common/Button';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/common/Select';
import { Switch } from '@/components/common/Switch';
import { Textarea } from '@/components/common/Textarea';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/configs/constants';
import {
    BUSINESS_TYPE_OPTIONS,
    SPONSOR_TIER_OPTIONS
} from './LandmarkFormSheet';

// 共通 layout: 段落 box (= ItemMeoFieldsSection.tsx と同じ pattern)
function SectionBox({ title, hint, children }: { title: string, hint?: string, children: React.ReactNode }) {
    return (
        <div className='border border-background-6 !rounded-[5px] pb-4 mt-4'>
            <div className='px-6 py-3 text-[15px] font-semibold bg-background-6 rounded-t-sm'>
                {title}
            </div>
            {hint && <p className='px-6 mt-2 text-xs text-muted-foreground'>{hint}</p>}
            <div className='mt-4 px-6 space-y-3'>
                {children}
            </div>
        </div>
    );
}

const NONE_SENTINEL = '__none__';

// -----------------------------------------------------------------------------
// MEO 入稿 (= sponsor_tier / area_slug / sponsor_started_at / sponsor_paid_until)
// 常に表示 (= tier='none' でも sponsor_tier select は出す)
// -----------------------------------------------------------------------------

// tier 別の機能差。design/seo-landmark-spec.md §1.3.2 / business-plan-revenue-portfolio-20260503.md §3.2 と同期。
// UI 表示用の要約 (詳細仕様は spec を真とする)。
const TIER_FEATURES: Record<string, { headline: string, items: string[] }> = {
    none: {
        headline: 'SEO ページは noindex (Google 非公開)',
        items: ['= 既存 landmark 動作。MEO 機能を使う場合のみ上位階層へ']
    },
    standard: {
        headline: '専用 SEO ページ + 月次レポート',
        items: [
            '専用ページ生成 + 構造化データ (JSON-LD)',
            '写真 1 枚 / メニュー 10 件まで掲載',
            'sitemap 登録 + 月次検索順位レポート',
            'sponsor pin 1 個'
        ]
    },
    premium: {
        headline: 'スタンダード + 動的優先 + クーポン枠拡張',
        items: [
            'スタンダードの全機能',
            'sponsor pin 動的優先表示',
            'クーポン枠 月 100 件無料 (超過は ¥30/件)',
            '業種 (Schema.org type) 上書き可',
            '写真 5 枚まで掲載'
        ]
    },
    enterprise: {
        headline: 'プレミアム + 専属 dashboard + マーケ支援',
        items: [
            'プレミアムの全機能',
            '専属 dashboard (来店推定 / 競合比較 / 動線分析)',
            '月 1 回のマーケアドバイザリ',
            '限定モチーフ コラボ制作 (年 1 回まで)',
            'カスタム title / description'
        ]
    }
};

function TierFeatureCard({ tier }: { tier: string }) {
    const feat = TIER_FEATURES[tier] ?? TIER_FEATURES.none;
    return (
        <div className='rounded-md border border-background-6 bg-background-6/40 px-4 py-3 mt-2'>
            <p className='text-xs font-semibold mb-1'>この階層で使える機能</p>
            <p className='text-xs text-muted-foreground mb-2'>{feat.headline}</p>
            <ul className='list-disc pl-4 text-xs text-muted-foreground space-y-0.5'>
                {feat.items.map((it, i) => (
                    <li key={i}>{it}</li>
                ))}
            </ul>
        </div>
    );
}

export function MeoEntrySection({ form }: { form: any }) {
    const tierWatched: string = form.watch('sponsorTier') || 'none';
    return (
        <SectionBox
            title='MEO 設定 (Google 検索 / SEO)'
            hint='スポンサー階層を選ぶと、追加の店舗情報セクションが表示されます。「なし」を選ぶと SEO ページは Google に掲載されません。'
        >
            <FormField
                control={form.control}
                name='sponsorTier'
                render={({ field }: any) => (
                    <FormItem>
                        <Label>スポンサー階層 *</Label>
                        <FormControl>
                            <Select
                                onValueChange={field.onChange}
                                value={typeof field.value === 'string' ? field.value : 'none'}
                            >
                                <SelectTrigger className='mt-2'>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {SPONSOR_TIER_OPTIONS.map(o => (
                                        <SelectItem
                                            key={o.value}
                                            value={o.value}
                                            className='hover:cursor-pointer'
                                        >
                                            {o.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormControl>
                        <TierFeatureCard tier={tierWatched} />
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name='areaSlug'
                render={({ field }: any) => (
                    <FormItem>
                        <Label>エリアコード (任意)</Label>
                        <p className='text-xs text-muted-foreground'>
                            エリア一覧ページ ( /landmark/area/&lt;エリアコード&gt; ) の URL に使われ、
                            「那覇 国際通り」等の地名検索の SEO 評価対象になります。英小文字 + 数字 + ハイフンのみ。空欄可。
                        </p>
                        <FormControl>
                            <Input
                                placeholder='例: naha-kokusaidori'
                                {...field}
                                value={field.value ?? ''}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <div className='grid grid-cols-2 gap-3'>
                <FormField
                    control={form.control}
                    name='sponsorStartedAt'
                    render={({ field }: any) => (
                        <FormItem>
                            <Label>課金開始日</Label>
                            <FormControl>
                                <Input
                                    type='date'
                                    {...field}
                                    value={field.value ?? ''}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name='sponsorPaidUntil'
                    render={({ field }: any) => (
                        <FormItem>
                            <Label>次回更新期限</Label>
                            <FormControl>
                                <Input
                                    type='date'
                                    {...field}
                                    value={field.value ?? ''}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
        </SectionBox>
    );
}

// -----------------------------------------------------------------------------
// 店舗情報 (= 住所 / 電話 / business_type / 紹介 / SEO description)
// sponsor_tier !== 'none' で表示
// -----------------------------------------------------------------------------

export function ShopInfoSection({ form }: { form: any }) {
    return (
        <SectionBox title='店舗情報'>
            <div className='grid grid-cols-2 gap-3'>
                <FormField
                    control={form.control}
                    name='postalCode'
                    render={({ field }: any) => (
                        <FormItem>
                            <Label>郵便番号</Label>
                            <FormControl>
                                <Input
                                    placeholder='900-0001'
                                    {...field}
                                    value={field.value ?? ''}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name='phone'
                    render={({ field }: any) => (
                        <FormItem>
                            <Label>電話番号</Label>
                            <FormControl>
                                <Input
                                    placeholder='098-000-0000'
                                    {...field}
                                    value={field.value ?? ''}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            <FormField
                control={form.control}
                name='addressRegion'
                render={({ field }: any) => (
                    <FormItem>
                        <Label>都道府県</Label>
                        <FormControl>
                            <Input
                                placeholder='沖縄県'
                                {...field}
                                value={field.value ?? ''}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name='addressCity'
                render={({ field }: any) => (
                    <FormItem>
                        <Label>市区町村</Label>
                        <FormControl>
                            <Input
                                placeholder='那覇市'
                                {...field}
                                value={field.value ?? ''}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name='addressStreet'
                render={({ field }: any) => (
                    <FormItem>
                        <Label>番地 / 建物</Label>
                        <FormControl>
                            <Input
                                placeholder='美栄橋 1-2-3 ビル 2F'
                                {...field}
                                value={field.value ?? ''}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name='businessType'
                render={({ field }: any) => (
                    <FormItem>
                        <Label>業種 (プレミアム以上で上書き推奨)</Label>
                        <FormControl>
                            <Select
                                onValueChange={(v: string) => field.onChange(v === NONE_SENTINEL ? '' : v)}
                                value={field.value ? String(field.value) : NONE_SENTINEL}
                            >
                                <SelectTrigger className='mt-2'>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {BUSINESS_TYPE_OPTIONS.map(o => (
                                        <SelectItem
                                            key={o.value || 'none'}
                                            value={o.value || NONE_SENTINEL}
                                            className='hover:cursor-pointer'
                                        >
                                            {o.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name='description'
                render={({ field }: any) => (
                    <FormItem>
                        <Label>紹介文 (200 文字以内)</Label>
                        <FormControl>
                            <Textarea
                                placeholder='お店 / スポットの紹介。ユーザ向け。'
                                {...field}
                                value={field.value ?? ''}
                                maxLength={200}
                                rows={4}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name='seoDescription'
                render={({ field }: any) => (
                    <FormItem>
                        <Label>Google 検索結果の説明文 (160 文字、空欄可)</Label>
                        <p className='text-xs text-muted-foreground'>
                            Google 検索結果でタイトル下に表示される短い説明 (= meta description)。
                            空欄なら上の「紹介文」が代わりに使われます。検索キーワードを前半に含めると効果的。
                        </p>
                        <FormControl>
                            <Textarea
                                placeholder='例: 那覇 国際通りの両替・カフェ。紅型おみくじでお得なクーポンを獲得できる人気スポット。'
                                {...field}
                                value={field.value ?? ''}
                                maxLength={160}
                                rows={3}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
        </SectionBox>
    );
}

// -----------------------------------------------------------------------------
// Phase 2 PR 6 (2026-05-15): coupon_by_fortune 入稿 UI 撤去。
//
// 旧 PoC の信用ベース JSON 入稿 UI は Phase 1-3 を通じて
// 「coupon_templates + coupon_batch_codes (= 事業者 POS 登録済み code を batch 入稿、
//   drawOmikuji が atomic claim)」 に置換済 (= release 1d70ab1 本番反映済)。
//
// 本セクションは新管理画面 /coupon-templates への誘導 + 当該 landmark の template
// 一覧の縮小版を表示する。「+ クーポンテンプレート追加」 button で新管理画面に遷移。
//
// landmark.metadata.coupon_by_fortune の DB 列削除 + drawOmikuji 旧 fallback 削除は
// Phase 2 PR 8 (= user 明示承認後に起票)。本 PR ではあくまで UI 撤去のみで、
// LandmarkFormSheet の form data flow (= 既存値の保持) は無改修。
//
// 旧 CouponByFortuneSection は export を削除 (= dead code は PR 8 で物理削除予定)。
// -----------------------------------------------------------------------------

const FORTUNE_LABEL: Record<string, string> = {
    '1': '大吉', '2': '中吉', '3': '小吉', '4': '末吉', '5': '凶'
};

const formatFortuneLabel = (f: number | null | undefined): string => {
    if (f === null || f === undefined) return '共通プール';
    return FORTUNE_LABEL[String(f)] ?? `(${f})`;
};

export const CouponTemplatesSection = observer(function CouponTemplatesSection({ landmark }: { landmark: any }) {
    const {
        couponTemplateStore: { list: fetchList }
    } = useStore();

    const [templates, setTemplates] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => {
            if (!landmark?.id) return;
            setLoading(true);
            try {
                const res = await flowResult(fetchList({ landmark_id: landmark.id }));
                if (active && res?.ok) {
                    setTemplates(res.data?.items ?? []);
                }
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [landmark?.id, fetchList]);

    return (
        <SectionBox
            title='クーポンテンプレート (= 新管理画面で運用)'
            hint='事業者が POS / 紙券で持っているクーポンコードを CMS にまとめて投入し、利用者の shake で 1 件ずつ配る仕組みです。新規追加・コード投入・在庫確認は専用管理画面で行います。'
        >
            {!landmark?.id ?
                (
                    <p className='text-xs text-muted-foreground'>
                        landmark 保存後に紐付け可能になります。
                    </p>
                ) :
                loading ?
                    (
                        <p className='text-xs text-muted-foreground'>読み込み中…</p>
                    ) :
                    templates.length === 0 ?
                        (
                            <div className='text-xs text-muted-foreground space-y-2'>
                                <p>この landmark にはまだクーポンテンプレートが紐付いていません。</p>
                                <Link
                                    to={ROUTES.couponTemplateManagement.href}
                                    className='inline-block text-blue-600 hover:text-blue-800 underline'
                                >
                                    クーポンテンプレート管理画面を開く →
                                </Link>
                            </div>
                        ) :
                        (
                            <div className='space-y-2'>
                                <div className='overflow-x-auto border border-background-6 rounded'>
                                    <table className='w-full text-xs'>
                                        <thead>
                                            <tr className='bg-background-6/60 text-left'>
                                                <th className='py-1.5 px-2'>fortune</th>
                                                <th className='py-1.5 px-2'>名称</th>
                                                <th className='py-1.5 px-2 text-right'>発行</th>
                                                <th className='py-1.5 px-2 text-right'>消費</th>
                                                <th className='py-1.5 px-2 text-right'>available</th>
                                                <th className='py-1.5 px-2'>active</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {templates.map((tpl) => {
                                                // 2026-05-16 (Issue 3): 在庫を 3 段階で色分け (= 一覧 page と同 logic)
                                                const n = tpl.available_count ?? 0;
                                                const isOut = n === 0;
                                                const isLow = !isOut && tpl.low_stock_alert;
                                                const rowBg = isOut ? 'bg-red-50' : isLow ? 'bg-amber-50' : '';
                                                const stockCls = isOut ?
                                                    'text-red-800 font-bold' :
                                                    isLow ? 'text-amber-700 font-medium' : 'text-emerald-700 font-medium';
                                                return (
                                                    <tr
                                                        key={tpl.id}
                                                        className={`border-t border-background-6 ${rowBg}`}
                                                    >
                                                        <td className='py-1.5 px-2'>
                                                            {formatFortuneLabel(tpl.fortune)}
                                                        </td>
                                                        <td className='py-1.5 px-2 font-medium'>{tpl.name}</td>
                                                        <td className='py-1.5 px-2 text-right'>{tpl.total_issued ?? 0}</td>
                                                        <td className='py-1.5 px-2 text-right'>{tpl.total_consumed ?? 0}</td>
                                                        <td className='py-1.5 px-2 text-right'>
                                                            <span className={stockCls}>
                                                                {isOut ? '0 在庫切れ' : n}
                                                            </span>
                                                        </td>
                                                        <td className='py-1.5 px-2'>{tpl.is_active ? '✓' : '×'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <Link
                                    to={ROUTES.couponTemplateManagement.href}
                                    className='inline-block text-xs text-blue-600 hover:text-blue-800 underline'
                                >
                                    クーポンテンプレート管理画面で詳細編集 →
                                </Link>
                            </div>
                        )}
        </SectionBox>
    );
});

// -----------------------------------------------------------------------------
// 営業時間 (= Schema.org OpeningHoursSpecification を曜日 × 時間帯 UI で受ける)
// 内部値は `openingHoursJson` (JSON 文字列) のまま、UI は曜日 grid + time picker。
// 同じ曜日に複数時間帯 (例: 11-14 / 17-22 のランチ・ディナー) のデータは
// 簡易 UI では表現できないため、自動で JSON 直編集モードへ fallback。
// -----------------------------------------------------------------------------

const DAY_LABELS_JP: { key: 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday', label: string }[] = [
    { key: 'Monday', label: '月' },
    { key: 'Tuesday', label: '火' },
    { key: 'Wednesday', label: '水' },
    { key: 'Thursday', label: '木' },
    { key: 'Friday', label: '金' },
    { key: 'Saturday', label: '土' },
    { key: 'Sunday', label: '日' }
];

type DayKey = typeof DAY_LABELS_JP[number]['key'];

interface DayState {
    enabled: boolean, opens: string, closes: string
}
type DaysState = Record<DayKey, DayState>;

const DEFAULT_DAY: DayState = { enabled: false, opens: '09:00', closes: '18:00' };

const initDaysState = (): DaysState =>
    Object.fromEntries(DAY_LABELS_JP.map(d => [d.key, { ...DEFAULT_DAY }])) as DaysState;

function parseFromJson(json: string | null | undefined): DaysState {
    const state = initDaysState();
    if (!json) return state;
    try {
        const arr = JSON.parse(json);
        if (!Array.isArray(arr)) return state;
        for (const entry of arr) {
            if (!entry || typeof entry !== 'object') continue;
            const days = Array.isArray(entry.dayOfWeek) ? entry.dayOfWeek : [entry.dayOfWeek];
            for (const d of days) {
                if (typeof d !== 'string') continue;
                if (state[d as DayKey]) {
                    state[d as DayKey] = {
                        enabled: true,
                        opens: typeof entry.opens === 'string' ? entry.opens : DEFAULT_DAY.opens,
                        closes: typeof entry.closes === 'string' ? entry.closes : DEFAULT_DAY.closes
                    };
                }
            }
        }
    } catch {
        /* fall through to default state */
    }
    return state;
}

function serializeToJson(state: DaysState): string | null {
    // 同じ opens/closes の曜日をまとめて 1 entry に集約
    const buckets = new Map<string, DayKey[]>();
    for (const { key } of DAY_LABELS_JP) {
        const row = state[key];
        if (!row.enabled) continue;
        const sig = `${row.opens}~${row.closes}`;
        const existing = buckets.get(sig) ?? [];
        existing.push(key);
        buckets.set(sig, existing);
    }
    if (buckets.size === 0) return null;
    const arr = Array.from(buckets.entries()).map(([sig, days]) => {
        const [opens, closes] = sig.split('~');
        return {
            dayOfWeek: days.length === 1 ? days[0] : days,
            opens,
            closes
        };
    });
    return JSON.stringify(arr, null, 2);
}

// 同一曜日に複数時間帯 (= Multi-span) があるかを検出。簡易 UI では編集不可。
function hasMultiSpan(json: string | null | undefined): boolean {
    if (!json) return false;
    try {
        const arr = JSON.parse(json);
        if (!Array.isArray(arr)) return false;
        const seen = new Set<string>();
        for (const e of arr) {
            if (!e || typeof e !== 'object') continue;
            const days = Array.isArray(e.dayOfWeek) ? e.dayOfWeek : [e.dayOfWeek];
            for (const d of days) {
                if (typeof d !== 'string') continue;
                if (seen.has(d)) return true;
                seen.add(d);
            }
        }
        return false;
    } catch {
        return false;
    }
}

export function OpeningHoursSection({ form }: { form: any }) {
    const initialJson = form.getValues('openingHoursJson');
    const [mode, setMode] = useState<'simple' | 'json'>(
        () => (hasMultiSpan(initialJson) ? 'json' : 'simple')
    );

    const toggleMode = () => {
        if (mode === 'json') {
            const cur = form.getValues('openingHoursJson');
            if (hasMultiSpan(cur)) {
                const ok = window.confirm(
                    '簡易 UI は曜日ごとに 1 つの時間帯のみ表示できます。\n複数時間帯 (例: ランチ / ディナー) のデータは失われます。\n続行しますか?'
                );
                if (!ok) return;
            }
        }
        setMode(mode === 'simple' ? 'json' : 'simple');
    };

    return (
        <SectionBox
            title='営業時間'
            hint={
                mode === 'simple' ?
                    '曜日ごとに開店・閉店時間を入力。同じ時間帯の曜日は保存時に自動でまとめられます。' :
                    'Schema.org OpeningHoursSpecification 形式 JSON。同じ曜日に複数時間帯がある場合の上級者向け編集。'
            }
        >
            <div className='flex justify-end mb-2'>
                <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={toggleMode}
                >
                    {mode === 'simple' ? 'JSON で直接編集' : '簡易 UI に戻る'}
                </Button>
            </div>
            {mode === 'simple' ?
                <SimpleDaysGrid form={form} /> :
                <JsonOpeningHoursField form={form} />}
        </SectionBox>
    );
}

function SimpleDaysGrid({ form }: { form: any }) {
    const watched = useWatch({ control: form.control, name: 'openingHoursJson' });
    const days = useMemo(() => parseFromJson(watched), [watched]);

    const apply = (next: DaysState) => {
        form.setValue('openingHoursJson', serializeToJson(next), { shouldDirty: true });
    };

    const update = (key: DayKey, patch: Partial<DayState>) => {
        apply({ ...days, [key]: { ...days[key], ...patch } });
    };

    const applyPreset = (preset: 'all-9-18' | 'weekday-9-18' | '24h' | 'reset') => {
        const next = initDaysState();
        const set = (k: DayKey, opens: string, closes: string) => {
            next[k] = { enabled: true, opens, closes };
        };
        if (preset === 'all-9-18') {
            DAY_LABELS_JP.forEach(d => set(d.key, '09:00', '18:00'));
        } else if (preset === 'weekday-9-18') {
            (['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] as DayKey[]).forEach(d => set(d, '09:00', '18:00'));
        } else if (preset === '24h') {
            DAY_LABELS_JP.forEach(d => set(d.key, '00:00', '23:59'));
        }
        apply(next);
    };

    return (
        <div className='space-y-2'>
            {DAY_LABELS_JP.map(({ key, label }) => {
                const row = days[key];
                return (
                    <div key={key} className='flex items-center gap-3 py-1'>
                        <span className='w-6 text-sm font-medium'>{label}</span>
                        <Switch
                            checked={row.enabled}
                            onCheckedChange={(v: boolean) => update(key, { enabled: v })}
                        />
                        <span className='text-xs text-muted-foreground w-10'>
                            {row.enabled ? '営業' : '定休'}
                        </span>
                        <Input
                            type='time'
                            value={row.opens}
                            disabled={!row.enabled}
                            onChange={e => update(key, { opens: e.target.value })}
                            className='w-28'
                        />
                        <span className='text-xs text-muted-foreground'>〜</span>
                        <Input
                            type='time'
                            value={row.closes}
                            disabled={!row.enabled}
                            onChange={e => update(key, { closes: e.target.value })}
                            className='w-28'
                        />
                    </div>
                );
            })}

            <div className='flex flex-wrap gap-2 mt-3 pt-3 border-t'>
                <span className='text-xs text-muted-foreground self-center mr-1'>クイック設定:</span>
                <Button type='button' variant='outline' size='sm' onClick={() => applyPreset('all-9-18')}>
                    全日 9:00-18:00
                </Button>
                <Button type='button' variant='outline' size='sm' onClick={() => applyPreset('weekday-9-18')}>
                    平日のみ 9:00-18:00
                </Button>
                <Button type='button' variant='outline' size='sm' onClick={() => applyPreset('24h')}>
                    24 時間営業
                </Button>
                <Button type='button' variant='outline' size='sm' onClick={() => applyPreset('reset')}>
                    全日定休
                </Button>
            </div>
        </div>
    );
}

function JsonOpeningHoursField({ form }: { form: any }) {
    const example = JSON.stringify(
        [
            { dayOfWeek: ['Monday', 'Tuesday'], opens: '11:00', closes: '14:00' },
            { dayOfWeek: ['Monday', 'Tuesday'], opens: '17:00', closes: '22:00' }
        ],
        null,
        2
    );
    return (
        <FormField
            control={form.control}
            name='openingHoursJson'
            render={({ field }: any) => (
                <FormItem>
                    <Label>opening_hours_spec (JSON 配列)</Label>
                    <FormControl>
                        <Textarea
                            placeholder={example}
                            {...field}
                            value={field.value ?? ''}
                            rows={8}
                            className='font-mono text-xs'
                        />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}

// -----------------------------------------------------------------------------
// SNS / 公式リンク
// -----------------------------------------------------------------------------

export function SnsLinksSection({ form }: { form: any }) {
    return (
        <SectionBox
            title='SNS / 公式リンク'
            hint='URL を入力。空欄は SEO ページに表示されません。'
        >
            <FormField
                control={form.control}
                name='snsInstagram'
                render={({ field }: any) => (
                    <FormItem>
                        <Label>Instagram</Label>
                        <FormControl>
                            <Input
                                type='url'
                                placeholder='https://instagram.com/...'
                                {...field}
                                value={field.value ?? ''}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name='snsTwitter'
                render={({ field }: any) => (
                    <FormItem>
                        <Label>Twitter / X</Label>
                        <FormControl>
                            <Input
                                type='url'
                                placeholder='https://twitter.com/...'
                                {...field}
                                value={field.value ?? ''}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name='snsFacebook'
                render={({ field }: any) => (
                    <FormItem>
                        <Label>Facebook</Label>
                        <FormControl>
                            <Input
                                type='url'
                                placeholder='https://facebook.com/...'
                                {...field}
                                value={field.value ?? ''}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name='snsWebsite'
                render={({ field }: any) => (
                    <FormItem>
                        <Label>公式サイト</Label>
                        <FormControl>
                            <Input
                                type='url'
                                placeholder='https://example.jp'
                                {...field}
                                value={field.value ?? ''}
                            />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
        </SectionBox>
    );
}

// -----------------------------------------------------------------------------
// メニュー / 商品リスト (useFieldArray)
// -----------------------------------------------------------------------------

interface FieldArrayProps {
    form: any,
    fieldArray: any,
    limit: number
}

export function MenuItemsSection({ form, fieldArray, limit }: FieldArrayProps) {
    const { fields, append, remove } = fieldArray;
    return (
        <SectionBox
            title={`メニュー / 商品リスト (最大 ${limit} 件)`}
            hint='SEO ページの "メニュー / 商品" セクションに掲載されます。空欄行は保存時に除外。'
        >
            {fields.length === 0 && (
                <p className='text-xs text-muted-foreground py-2'>(まだ登録なし)</p>
            )}
            {fields.map((f: any, idx: number) => (
                <div
                    key={f.id}
                    className='border border-background-6 rounded-md p-3 space-y-2'
                >
                    <div className='flex justify-between items-center'>
                        <span className='text-xs text-muted-foreground'>
                            {`#${idx + 1}`}
                        </span>
                        <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            onClick={() => remove(idx)}
                        >
                            削除
                        </Button>
                    </div>
                    <FormField
                        control={form.control}
                        name={`menuItems.${idx}.name` as any}
                        render={({ field }: any) => (
                            <FormItem>
                                <Label>名前 *</Label>
                                <FormControl>
                                    <Input
                                        placeholder='例: コーヒー'
                                        {...field}
                                        value={field.value ?? ''}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name={`menuItems.${idx}.price` as any}
                        render={({ field }: any) => (
                            <FormItem>
                                <Label>価格</Label>
                                <FormControl>
                                    <Input
                                        placeholder='例: ¥500'
                                        {...field}
                                        value={field.value ?? ''}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name={`menuItems.${idx}.description` as any}
                        render={({ field }: any) => (
                            <FormItem>
                                <Label>説明 (任意、200 文字)</Label>
                                <FormControl>
                                    <Textarea
                                        rows={2}
                                        {...field}
                                        value={field.value ?? ''}
                                        maxLength={200}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name={`menuItems.${idx}.image_url` as any}
                        render={({ field }: any) => (
                            <FormItem>
                                <Label>画像 URL (任意)</Label>
                                <FormControl>
                                    <Input
                                        type='url'
                                        placeholder='https://...'
                                        {...field}
                                        value={field.value ?? ''}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
            ))}
            <Button
                type='button'
                variant='outline'
                onClick={() => append({ name: '', price: '', description: '', image_url: '' })}
                disabled={fields.length >= limit}
            >
                {`+ メニュー追加 (${fields.length}/${limit})`}
            </Button>
        </SectionBox>
    );
}

// -----------------------------------------------------------------------------
// 写真 (URL 配列、useFieldArray)
// -----------------------------------------------------------------------------

export function ImagesSection({ form, fieldArray, limit }: FieldArrayProps) {
    const { fields, append, remove } = fieldArray;
    return (
        <SectionBox
            title={`写真 URL (最大 ${limit} 枚)`}
            hint='現状は URL を直接入力する形式 (= MVP)。CDN や /public/postcards 配下の絶対 URL を貼ります。将来的にファイルアップロード UI 化を検討。'
        >
            {fields.length === 0 && (
                <p className='text-xs text-muted-foreground py-2'>(まだ登録なし)</p>
            )}
            {fields.map((f: any, idx: number) => (
                <div
                    key={f.id}
                    className='flex gap-2 items-start'
                >
                    <FormField
                        control={form.control}
                        name={`images.${idx}.url` as any}
                        render={({ field }: any) => (
                            <FormItem className='flex-1'>
                                <Label>{`#${idx + 1}`}</Label>
                                <FormControl>
                                    <Input
                                        type='url'
                                        placeholder='https://...'
                                        {...field}
                                        value={field.value ?? ''}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => remove(idx)}
                        className='mt-6'
                    >
                        削除
                    </Button>
                </div>
            ))}
            <Button
                type='button'
                variant='outline'
                onClick={() => append({ url: '' })}
                disabled={fields.length >= limit}
            >
                {`+ 写真 URL 追加 (${fields.length}/${limit})`}
            </Button>
        </SectionBox>
    );
}

// -----------------------------------------------------------------------------
// MEO Phase 1 Sprint 2-F (2026-05-13): レビューリワード戦略 セクション
// landmark.metadata.review_bonus_strategy を個別 endpoint で更新する
// (= PUT /system/landmarks/:id/review-bonus-strategy)。
// 編集 mode (= landmark.id 存在) でのみ表示。
// Spec: design/meo-landmark-ads-spec.md §14 / §戦略モード一覧
// -----------------------------------------------------------------------------

// 2026-05-16: 業務 user 向けに label / hint を平易化。
// 技術用語 (= bundle_upgrade / bundle_base / digital_unlock 等) は内部値として残し、表示は日本語化。
const REVIEW_STRATEGY_MODE_OPTIONS = [
    {
        value: 'bundle_upgrade',
        label: 'クーポンを切替える (= 周辺スポット案内も切替)',
        hint: 'レビュー送信時に、最初に渡したクーポンを「特別クーポン」 に切替えます。周辺スポットの案内も「通常版 → アップグレード版」 に切替わります。クーポンテンプレート編集画面の「レビュー後に切替えるクーポン」 と、各周辺スポットの「アップグレード後の特典」 欄を埋めてください。',
        fields: { rate: false, public: false, digital: false }
    },
    {
        value: 'rate_upgrade_only',
        label: '両替レートを優遇',
        hint: 'レビュー送信からの一定時間 (= bonus_duration_minutes)、この landmark の両替レートに +extra_percent% (上限 max_yen 円) を上乗せします。',
        fields: { rate: true, public: false, digital: false }
    },
    {
        value: 'rate_upgrade_plus_public_guide',
        label: 'レート優遇 + 公共スポット案内',
        hint: 'レート優遇に加え、観光協会等の「公共スポット」 (= 協力店舗ではない無料スポット) の案内を解放します。許諾不要で常に使えるため default 推奨。',
        fields: { rate: true, public: true, digital: false }
    },
    {
        value: 'digital_only',
        label: 'デジタル特典のみ',
        hint: '当地非依存のデジタル特典 (PDF / 動画 / ダウンロード等) を解放。下の「digital_unlock」 欄に項目 ID を行ごとに記載してください。',
        fields: { rate: false, public: false, digital: true }
    },
    {
        value: 'rate_upgrade_plus_digital',
        label: 'レート優遇 + デジタル特典',
        hint: '両替レート優遇とデジタル特典を組み合わせ。',
        fields: { rate: true, public: false, digital: true }
    }
];

interface ReviewBonusStrategyProps {
    landmark: any,
    onSaved?: () => void
}

export const ReviewBonusStrategySection = observer(function ReviewBonusStrategySection({
    landmark, onSaved
}: ReviewBonusStrategyProps) {
    const { landmarkStore } = useStore();

    const initial = useMemo(() => {
        const s = landmark?.metadata?.review_bonus_strategy || {};
        return {
            mode: s.mode || 'bundle_upgrade',
            extra_percent: s.rate_upgrade?.extra_percent != null ? String(s.rate_upgrade.extra_percent) : '',
            max_yen: s.rate_upgrade?.max_yen != null ? String(s.rate_upgrade.max_yen) : '',
            public_spots_unlock: !!s.public_spots_unlock,
            digital_unlock_text: Array.isArray(s.digital_unlock) ? s.digital_unlock.join('\n') : '',
            bonus_duration_minutes: s.bonus_duration_minutes != null ? String(s.bonus_duration_minutes) : '60'
        };
    }, [landmark]);

    const [mode, setMode] = useState<string>(initial.mode);
    const [extraPercent, setExtraPercent] = useState<string>(initial.extra_percent);
    const [maxYen, setMaxYen] = useState<string>(initial.max_yen);
    const [publicSpotsUnlock, setPublicSpotsUnlock] = useState<boolean>(initial.public_spots_unlock);
    const [digitalUnlockText, setDigitalUnlockText] = useState<string>(initial.digital_unlock_text);
    const [bonusDurationMinutes, setBonusDurationMinutes] = useState<string>(initial.bonus_duration_minutes);
    const [saving, setSaving] = useState<boolean>(false);

    // landmark 切替時に initial を反映
    useEffect(() => {
        setMode(initial.mode);
        setExtraPercent(initial.extra_percent);
        setMaxYen(initial.max_yen);
        setPublicSpotsUnlock(initial.public_spots_unlock);
        setDigitalUnlockText(initial.digital_unlock_text);
        setBonusDurationMinutes(initial.bonus_duration_minutes);
    }, [initial]);

    const modeOption = REVIEW_STRATEGY_MODE_OPTIONS.find(o => o.value === mode) ?? REVIEW_STRATEGY_MODE_OPTIONS[0];
    const showRate = modeOption.fields.rate;
    const showPublic = modeOption.fields.public;
    const showDigital = modeOption.fields.digital;

    const handleSave = async () => {
        if (!landmark?.id) return;
        const body: any = { mode };
        if (showRate) {
            // 2026-05-16: rate-related mode では extra_percent / max_yen を必須化する。
            // 旧実装は blank → Number('')=0 で通過し、 backend で 0 が保存されて enduser
            // 側に「+0% (上限 0 円)」 が表示される or default fallback が表示される。 user 視点で
            // 「CMS 未入力で enduser に値が出る」 のは混乱の元 (= 2026-05-16 user 報告)。
            if (extraPercent.trim() === '' || maxYen.trim() === '') {
                toastify(
                    'error',
                    'この mode では「レートアップ %%」 と「上限金額 (円)」 の両方を入力してください'
                );
                return;
            }
            const ep = Number(extraPercent);
            const my = Number(maxYen);
            if (!Number.isFinite(ep) || ep < 0) {
                toastify('error', 'レートアップ %% は 0 以上の数値を入力してください');
                return;
            }
            if (!Number.isFinite(my) || my < 0) {
                toastify('error', '上限金額は 0 以上の数値を入力してください');
                return;
            }
            body.rate_upgrade = { extra_percent: ep, max_yen: my };
        }
        if (showPublic) {
            body.public_spots_unlock = publicSpotsUnlock;
        }
        if (showDigital) {
            body.digital_unlock = digitalUnlockText
                .split('\n')
                .map(s => s.trim())
                .filter(s => s.length > 0);
        }
        const dur = Number(bonusDurationMinutes);
        if (!Number.isFinite(dur) || dur < 0) {
            toastify('error', 'bonus 持続時間 (分) は 0 以上を入力してください');
            return;
        }
        body.bonus_duration_minutes = dur;

        setSaving(true);
        try {
            const res: any = await flowResult(
                landmarkStore.updateReviewBonusStrategy({ id: landmark.id, data: body })
            );
            if (res?.ok) {
                toastify('success', 'レビューリワード戦略を保存しました');
                onSaved?.();
            } else {
                toastify('error', res?.message || '保存に失敗しました');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <SectionBox
            title='レビューリワード戦略'
            hint='レビュー (= Google Maps クリック) でユーザーに与える特典の構成。各 mode で必要なフィールドが切り替わる。'
        >
            <div className='space-y-3'>
                <div>
                    <Label>モード</Label>
                    <Select value={mode} onValueChange={setMode}>
                        <SelectTrigger>
                            <SelectValue placeholder='選択してください' />
                        </SelectTrigger>
                        <SelectContent>
                            {REVIEW_STRATEGY_MODE_OPTIONS.map(o => (
                                <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <p className='text-xs text-muted-foreground mt-1'>{modeOption.hint}</p>
                </div>

                {showRate && (
                    <div className='grid grid-cols-2 gap-3'>
                        <div>
                            <Label>
                                レート上乗せ %%
                                <span className='text-red-500'>*</span>
                            </Label>
                            <Input
                                type='number'
                                step='0.1'
                                min='0'
                                placeholder='例: 1.5'
                                value={extraPercent}
                                onChange={e => setExtraPercent(e.target.value)}
                            />
                            <p className='text-[10px] text-muted-foreground mt-1'>
                                通常レートに上乗せする %% (例: 1.5 で +1.5%%)。
                                未入力で保存しようとすると error toast が出ます。
                            </p>
                        </div>
                        <div>
                            <Label>
                                上限金額 (円)
                                <span className='text-red-500'>*</span>
                            </Label>
                            <Input
                                type='number'
                                step='1'
                                min='0'
                                placeholder='例: 500'
                                value={maxYen}
                                onChange={e => setMaxYen(e.target.value)}
                            />
                            <p className='text-[10px] text-muted-foreground mt-1'>
                                bonus の絶対上限 (= レート計算結果がこの値を超えても切捨)。
                                未入力で保存しようとすると error toast が出ます。
                            </p>
                        </div>
                    </div>
                )}

                {showPublic && (
                    <div className='flex items-center gap-2'>
                        <Switch
                            checked={publicSpotsUnlock}
                            onCheckedChange={setPublicSpotsUnlock}
                        />
                        <Label>公共スポット案内を解放</Label>
                    </div>
                )}

                {showDigital && (
                    <div>
                        <Label>デジタル特典 (1 行 1 項目)</Label>
                        <Textarea
                            rows={4}
                            placeholder={'例:\nokinawa-history-pdf\nbeach-walk-video'}
                            value={digitalUnlockText}
                            onChange={e => setDigitalUnlockText(e.target.value)}
                        />
                        <p className='text-[10px] text-muted-foreground mt-1'>
                            各行に digital unlock の項目 ID。空行は無視。
                        </p>
                    </div>
                )}

                <div>
                    <Label>bonus 持続時間 (分)</Label>
                    <Input
                        type='number'
                        step='1'
                        min='0'
                        placeholder='60'
                        value={bonusDurationMinutes}
                        onChange={e => setBonusDurationMinutes(e.target.value)}
                    />
                    <p className='text-[10px] text-muted-foreground mt-1'>
                        bonus が有効な時間 (例: 60 = レビュー後 60 分間)
                    </p>
                </div>

                <div className='flex justify-end'>
                    <Button
                        type='button'
                        variant='active'
                        onClick={handleSave}
                        disabled={saving || !landmark?.id}
                    >
                        {saving ? '保存中…' : '戦略を保存'}
                    </Button>
                </div>
            </div>
        </SectionBox>
    );
});

// -----------------------------------------------------------------------------
// MEO Phase 1 Sprint 2-F (2026-05-13): 周辺スポット管理 セクション
// landmark_neighborhoods CRUD UI (10 件上限)。
// 編集 mode (= landmark.id 存在) でのみ表示。
// Spec: design/meo-landmark-ads-spec.md §13 §周辺スポット入稿 / §エッジケース 32
// -----------------------------------------------------------------------------

const NEIGHBORHOODS_LIMIT = 10;

// MEO Phase 1 Sprint 2-F follow-up (2026-05-13):
// bundle_*_type は VARCHAR(32) free-form (= backend は validation せず保存)。
// CMS UI の誤入力 (全角/半角、typo) 防止のため dropdown 化。
// 既知 catalog に無い type は「その他 (カスタム入力)」 で escape 可 (= 後方互換維持)。
// 新種が頻発したら本 catalog に昇格 (= フォローアップ PR で 2-3 行追加)。
//
// 各 type の value 単位ヒントは入力欄の placeholder に動的反映される。
const BUNDLE_REWARD_TYPE_CATALOG = [
    { code: 'discount_yen', label: '円引き', valueHint: '金額 (円)、例: 100' },
    { code: 'percent_off', label: '% 引き', valueHint: 'パーセント (整数)、例: 10' },
    { code: 'free_item', label: '無料 / 試食', valueHint: '個数、例: 1 (シンボリックなら 0)' },
    { code: 'free_drink', label: 'ドリンク無料', valueHint: '杯数、例: 1' },
    { code: 'free_dessert', label: 'デザート無料', valueHint: '個数、例: 1' },
    { code: 'set_meal_upgrade', label: 'セット昇格', valueHint: 'シンボリック、例: 0' },
    { code: 'free_sample', label: 'サンプル提供', valueHint: 'シンボリック、例: 0' },
    { code: 'time_extension', label: '時間延長', valueHint: '分、例: 30' }
];
const BUNDLE_NONE_SENTINEL = '__bundle_none__';
const BUNDLE_CUSTOM_SENTINEL = '__bundle_custom__';

function deriveBundleTypeMode(typeCode: string): string {
    if (!typeCode) return BUNDLE_NONE_SENTINEL;
    if (BUNDLE_REWARD_TYPE_CATALOG.some(o => o.code === typeCode)) return typeCode;
    return BUNDLE_CUSTOM_SENTINEL;
}

function getBundleValueHint(typeCode: string): string {
    const opt = BUNDLE_REWARD_TYPE_CATALOG.find(o => o.code === typeCode);
    return opt?.valueHint ?? '値 (整数)';
}

interface BundleRewardRowProps {
    prefix: 'base' | 'upgrade',
    formState: any,
    setFormState: React.Dispatch<React.SetStateAction<any>>
}

function BundleRewardRow({ prefix, formState, setFormState }: BundleRewardRowProps) {
    const typeKey = `bundle_${prefix}_type`;
    const valueKey = `bundle_${prefix}_value`;
    const labelKey = `bundle_${prefix}_label_ja`;
    const heading = prefix === 'base' ? 'ベース (= 通常時)' : 'アップグレード (= レビュー後)';
    const currentType: string = formState[typeKey] ?? '';
    const dropdownValue = deriveBundleTypeMode(currentType);
    const isCustom = dropdownValue === BUNDLE_CUSTOM_SENTINEL;
    const valuePlaceholder = getBundleValueHint(currentType);

    const handleDropdownChange = (v: string) => {
        if (v === BUNDLE_NONE_SENTINEL) {
            setFormState((s: any) => ({ ...s, [typeKey]: '' }));
        } else if (v === BUNDLE_CUSTOM_SENTINEL) {
            // custom mode に入る時、既存値が catalog 内なら clear (= user に新規入力させる)
            setFormState((s: any) => {
                const inCatalog = BUNDLE_REWARD_TYPE_CATALOG.some(o => o.code === s[typeKey]);
                return inCatalog ? { ...s, [typeKey]: '' } : s;
            });
        } else {
            setFormState((s: any) => ({ ...s, [typeKey]: v }));
        }
    };

    return (
        <div className='mt-3'>
            <p className='text-[11px] font-semibold mb-1'>{heading}</p>
            <div className='grid grid-cols-3 gap-2'>
                <div>
                    <Label className='text-[10px]'>type</Label>
                    <Select value={dropdownValue} onValueChange={handleDropdownChange}>
                        <SelectTrigger>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={BUNDLE_NONE_SENTINEL}>(未設定 / 表示しない)</SelectItem>
                            {BUNDLE_REWARD_TYPE_CATALOG.map(o => (
                                <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
                            ))}
                            <SelectItem value={BUNDLE_CUSTOM_SENTINEL}>その他 (カスタム入力)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className='text-[10px]'>value</Label>
                    <Input
                        type='number'
                        placeholder={valuePlaceholder}
                        value={formState[valueKey]}
                        onChange={e => setFormState((s: any) => ({ ...s, [valueKey]: e.target.value }))}
                    />
                </div>
                <div>
                    <Label className='text-[10px]'>label (ja、enduser 表示用)</Label>
                    <Input
                        placeholder={prefix === 'base' ? 'コーヒー 100 円引き' : 'コーヒー 200 円引き (アップグレード)'}
                        value={formState[labelKey]}
                        onChange={e => setFormState((s: any) => ({ ...s, [labelKey]: e.target.value }))}
                    />
                </div>
            </div>
            {isCustom && (
                <div className='mt-2'>
                    <Label className='text-[10px]'>カスタム type コード (英数 / アンダーバー、例: free_topping)</Label>
                    <Input
                        placeholder='custom_type_code'
                        value={currentType}
                        onChange={e => setFormState((s: any) => ({ ...s, [typeKey]: e.target.value }))}
                    />
                    <p className='text-[10px] text-muted-foreground mt-1'>
                        ※ カスタム値はクロコの集計ロジックで個別対応されません。新種が複数 row で使われたら次回 PR で本カタログに昇格させてください。
                    </p>
                </div>
            )}
        </div>
    );
}

const SPOT_CATEGORY_OPTIONS = [
    { value: 'cafe', label: 'カフェ' },
    { value: 'restaurant', label: '飲食店' },
    { value: 'attraction', label: '観光スポット' },
    { value: 'shop', label: '店舗' },
    { value: 'public_attraction', label: '公共施設' },
    { value: 'museum', label: '美術館 / 博物館' },
    { value: 'other', label: 'その他' }
];

// 空フォーム (= 新規追加時の initial values)
function emptyNeighborhood() {
    return {
        spot_external_name: '',
        spot_category: 'cafe',
        walking_minutes: '',
        short_description_ja: '',
        has_partnership: false,
        is_public_attraction: false,
        spot_external_lat: '',
        spot_external_lng: '',
        spot_external_google_place_id: '',
        display_order: 0,
        is_active: true,
        bundle_base_type: '',
        bundle_base_value: '',
        bundle_base_label_ja: '',
        bundle_upgrade_type: '',
        bundle_upgrade_value: '',
        bundle_upgrade_label_ja: ''
    };
}

// row → form state 変換 (= edit 時に各 row の値を inline form に流す)
function rowToFormState(row: any) {
    const sd = row?.short_description_i18n || {};
    const blb = row?.bundle_base_label_i18n || {};
    const ulb = row?.bundle_upgrade_label_i18n || {};
    return {
        spot_external_name: row?.spot_external_name ?? '',
        spot_category: row?.spot_category ?? 'cafe',
        walking_minutes: row?.walking_minutes != null ? String(row.walking_minutes) : '',
        short_description_ja: sd.ja ?? '',
        has_partnership: !!row?.has_partnership,
        is_public_attraction: !!row?.is_public_attraction,
        spot_external_lat: row?.spot_external_lat != null ? String(row.spot_external_lat) : '',
        spot_external_lng: row?.spot_external_lng != null ? String(row.spot_external_lng) : '',
        spot_external_google_place_id: row?.spot_external_google_place_id ?? '',
        display_order: row?.display_order ?? 0,
        is_active: row?.is_active !== false,
        bundle_base_type: row?.bundle_base_type ?? '',
        bundle_base_value: row?.bundle_base_value != null ? String(row.bundle_base_value) : '',
        bundle_base_label_ja: blb.ja ?? '',
        bundle_upgrade_type: row?.bundle_upgrade_type ?? '',
        bundle_upgrade_value: row?.bundle_upgrade_value != null ? String(row.bundle_upgrade_value) : '',
        bundle_upgrade_label_ja: ulb.ja ?? ''
    };
}

// form state → API payload
function formStateToPayload(state: any) {
    const payload: any = {
        spot_external_name: state.spot_external_name?.trim() || null,
        spot_category: state.spot_category,
        walking_minutes: state.walking_minutes !== '' ? Number(state.walking_minutes) : null,
        has_partnership: !!state.has_partnership,
        is_public_attraction: !!state.is_public_attraction,
        is_active: state.is_active !== false,
        display_order: Number(state.display_order) || 0
    };
    // short_description_i18n
    const desc = (state.short_description_ja || '').trim();
    payload.short_description_i18n = desc ? { ja: desc } : null;
    // 緯度経度 / google_place_id (= 外部スポット用)
    payload.spot_external_lat = state.spot_external_lat !== '' ? Number(state.spot_external_lat) : null;
    payload.spot_external_lng = state.spot_external_lng !== '' ? Number(state.spot_external_lng) : null;
    payload.spot_external_google_place_id = state.spot_external_google_place_id?.trim() || null;
    // bundle terms
    payload.bundle_base_type = state.bundle_base_type?.trim() || null;
    payload.bundle_base_value = state.bundle_base_value !== '' ? Number(state.bundle_base_value) : null;
    const blbja = (state.bundle_base_label_ja || '').trim();
    payload.bundle_base_label_i18n = blbja ? { ja: blbja } : null;
    payload.bundle_upgrade_type = state.bundle_upgrade_type?.trim() || null;
    payload.bundle_upgrade_value = state.bundle_upgrade_value !== '' ? Number(state.bundle_upgrade_value) : null;
    const ulbja = (state.bundle_upgrade_label_ja || '').trim();
    payload.bundle_upgrade_label_i18n = ulbja ? { ja: ulbja } : null;
    return payload;
}

interface NeighborhoodsProps {
    landmark: any
}

export const NeighborhoodsSection = observer(function NeighborhoodsSection({ landmark }: NeighborhoodsProps) {
    const { landmarkNeighborhoodsStore: store, modalStore } = useStore();
    const [editingId, setEditingId] = useState<number | 'new' | null>(null);
    const [formState, setFormState] = useState<any>(emptyNeighborhood());
    const [saving, setSaving] = useState<boolean>(false);

    // landmark 切替時に list 再取得
    useEffect(() => {
        if (landmark?.id) {
            store.list(landmark.id);
            setEditingId(null);
        }
    }, [landmark?.id, store]);

    const handleStartNew = () => {
        setFormState(emptyNeighborhood());
        setEditingId('new');
    };

    const handleStartEdit = (row: any) => {
        setFormState(rowToFormState(row));
        setEditingId(Number(row.id));
    };

    const handleCancel = () => {
        setEditingId(null);
        setFormState(emptyNeighborhood());
    };

    const handleSave = async () => {
        if (!landmark?.id) return;
        // 必須項目チェック
        if (!formState.spot_external_name?.trim() && !formState.spot_external_google_place_id?.trim()) {
            toastify('error', 'スポット名 または Google Place ID のどちらかは必須です');
            return;
        }
        const payload = formStateToPayload(formState);

        setSaving(true);
        try {
            let res: any;
            if (editingId === 'new') {
                res = await flowResult(store.create({ landmarkId: landmark.id, data: payload }));
            } else if (typeof editingId === 'number') {
                res = await flowResult(store.update({ landmarkId: landmark.id, id: editingId, data: payload }));
            }
            if (res?.ok) {
                toastify('success', editingId === 'new' ? '周辺スポットを追加しました' : '周辺スポットを更新しました');
                await flowResult(store.list(landmark.id));
                setEditingId(null);
                setFormState(emptyNeighborhood());
            } else {
                toastify('error', res?.message || '保存に失敗しました');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (row: any) => {
        modalStore.showAlertModal({
            title: '削除確認',
            size: 'sm',
            content: `「${row.spot_external_name || row.id}」を削除しますか?`,
            onSave: async () => {
                const res: any = await flowResult(
                    store.remove({ landmarkId: landmark.id, id: row.id })
                );
                if (res?.ok) {
                    toastify('success', '削除しました');
                    await flowResult(store.list(landmark.id));
                } else {
                    toastify('error', res?.message || '削除に失敗しました');
                }
            }
        });
    };

    const rows = store.neighborhoods;
    const remaining = NEIGHBORHOODS_LIMIT - rows.length;
    const categoryLabel = (cat?: string) =>
        SPOT_CATEGORY_OPTIONS.find(o => o.value === cat)?.label ?? cat ?? '-';

    return (
        <SectionBox
            title={`周辺スポット管理 (${rows.length}/${NEIGHBORHOODS_LIMIT})`}
            hint='ユーザーに案内する周辺スポット。is_public_attraction=true は公共施設 (許諾不要)、has_partnership=true はバンドル協力店舗。display_order 順に enduser へ表示。'
        >
            {/* 既存スポット一覧 */}
            {rows.length === 0 && editingId !== 'new' && (
                <p className='text-xs text-muted-foreground py-2'>(まだ登録なし)</p>
            )}
            {rows.length > 0 && (
                <div className='overflow-x-auto'>
                    <table className='w-full text-xs'>
                        <thead>
                            <tr className='border-b border-gray-200 text-left'>
                                <th className='py-1 px-2 w-[40px]'>順</th>
                                <th className='py-1 px-2'>名称</th>
                                <th className='py-1 px-2'>カテゴリ</th>
                                <th className='py-1 px-2 text-right'>徒歩(分)</th>
                                <th className='py-1 px-2 text-center'>協力</th>
                                <th className='py-1 px-2 text-center'>公共</th>
                                <th className='py-1 px-2 text-center'>有効</th>
                                <th className='py-1 px-2 w-[110px]'>操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row: any) => (
                                <tr key={row.id} className='border-b border-gray-100'>
                                    <td className='py-1 px-2'>{row.display_order ?? 0}</td>
                                    <td className='py-1 px-2 font-medium'>
                                        {row.spot_external_name || `(internal landmark #${row.spot_landmark_id ?? '?'})`}
                                    </td>
                                    <td className='py-1 px-2'>{categoryLabel(row.spot_category)}</td>
                                    <td className='py-1 px-2 text-right'>{row.walking_minutes ?? '-'}</td>
                                    <td className='py-1 px-2 text-center'>{row.has_partnership ? '◯' : '-'}</td>
                                    <td className='py-1 px-2 text-center'>{row.is_public_attraction ? '◯' : '-'}</td>
                                    <td className='py-1 px-2 text-center'>{row.is_active === false ? '×' : '◯'}</td>
                                    <td className='py-1 px-2'>
                                        <div className='flex gap-1'>
                                            <Button
                                                type='button'
                                                variant='outline'
                                                size='sm'
                                                onClick={() => handleStartEdit(row)}
                                                disabled={editingId !== null}
                                            >
                                                編集
                                            </Button>
                                            <Button
                                                type='button'
                                                variant='outline'
                                                size='sm'
                                                onClick={() => handleDelete(row)}
                                                className='text-red-600 hover:text-red-800'
                                                disabled={editingId !== null}
                                            >
                                                削除
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* 追加 button (editing 中は disabled) */}
            {editingId === null && (
                <Button
                    type='button'
                    variant='outline'
                    onClick={handleStartNew}
                    disabled={remaining <= 0}
                    className='mt-2'
                >
                    {remaining > 0 ? `+ 周辺スポット追加 (残 ${remaining})` : '上限到達 (10 件)'}
                </Button>
            )}

            {/* inline form panel (editing 中だけ展開) */}
            {editingId !== null && (
                <div className='mt-3 border border-blue-300 rounded-md p-4 bg-blue-50/30 space-y-3'>
                    <p className='text-xs font-semibold text-blue-700'>
                        {editingId === 'new' ? '新規追加' : `編集 (id=${editingId})`}
                    </p>

                    <div className='grid grid-cols-2 gap-3'>
                        <div className='col-span-2'>
                            <Label>スポット名 (日本語)</Label>
                            <Input
                                placeholder='例: 那覇市役所'
                                value={formState.spot_external_name}
                                onChange={e => setFormState((s: any) => ({ ...s, spot_external_name: e.target.value }))}
                            />
                        </div>
                        <div>
                            <Label>カテゴリ</Label>
                            <Select
                                value={formState.spot_category}
                                onValueChange={v => setFormState((s: any) => ({ ...s, spot_category: v }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {SPOT_CATEGORY_OPTIONS.map(o => (
                                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>徒歩 (分)</Label>
                            <Input
                                type='number'
                                step='1'
                                min='0'
                                placeholder='5'
                                value={formState.walking_minutes}
                                onChange={e => setFormState((s: any) => ({ ...s, walking_minutes: e.target.value }))}
                            />
                        </div>
                        <div className='col-span-2'>
                            <Label>説明 (日本語、enduser 表示)</Label>
                            <Textarea
                                rows={2}
                                placeholder='例: 那覇の中心。観光案内マップが入手可。'
                                value={formState.short_description_ja}
                                onChange={e => setFormState((s: any) => ({ ...s, short_description_ja: e.target.value }))}
                            />
                        </div>
                        <div className='flex items-center gap-2'>
                            <Switch
                                checked={formState.has_partnership}
                                onCheckedChange={v => setFormState((s: any) => ({ ...s, has_partnership: v }))}
                            />
                            <Label>バンドル協力店舗</Label>
                        </div>
                        <div className='flex items-center gap-2'>
                            <Switch
                                checked={formState.is_public_attraction}
                                onCheckedChange={v => setFormState((s: any) => ({ ...s, is_public_attraction: v }))}
                            />
                            <Label>公共施設 (許諾不要)</Label>
                        </div>
                        <div>
                            <Label>表示順 (= 小さい順に上)</Label>
                            <Input
                                type='number'
                                step='1'
                                value={formState.display_order}
                                onChange={e => setFormState((s: any) => ({ ...s, display_order: e.target.value }))}
                            />
                        </div>
                        <div className='flex items-center gap-2'>
                            <Switch
                                checked={formState.is_active}
                                onCheckedChange={v => setFormState((s: any) => ({ ...s, is_active: v }))}
                            />
                            <Label>有効</Label>
                        </div>
                        <div>
                            <Label>緯度</Label>
                            <Input
                                type='number'
                                step='0.000001'
                                placeholder='26.21'
                                value={formState.spot_external_lat}
                                onChange={e => setFormState((s: any) => ({ ...s, spot_external_lat: e.target.value }))}
                            />
                        </div>
                        <div>
                            <Label>経度</Label>
                            <Input
                                type='number'
                                step='0.000001'
                                placeholder='127.68'
                                value={formState.spot_external_lng}
                                onChange={e => setFormState((s: any) => ({ ...s, spot_external_lng: e.target.value }))}
                            />
                        </div>
                        <div className='col-span-2'>
                            <Label>Google Place ID (任意)</Label>
                            <Input
                                placeholder='ChIJ...'
                                value={formState.spot_external_google_place_id}
                                onChange={e => setFormState((s: any) => ({ ...s, spot_external_google_place_id: e.target.value }))}
                            />
                        </div>
                    </div>

                    <div className='border-t border-blue-200 pt-3'>
                        <p className='text-xs font-semibold'>バンドル特典 (= 協力店舗の場合のみ)</p>
                        <p className='text-[10px] text-muted-foreground mt-1'>
                            ベース = 通常時、アップグレード = レビュー後の昇格時。type は dropdown から選択 (= タイポ防止)、value 単位は選んだ type に応じて placeholder に表示。新種が必要な場合は「その他 (カスタム入力)」 を選択 (= 後で本 catalog に昇格)。
                        </p>
                        <BundleRewardRow prefix='base' formState={formState} setFormState={setFormState} />
                        <BundleRewardRow prefix='upgrade' formState={formState} setFormState={setFormState} />
                    </div>

                    <div className='flex justify-end gap-2 pt-2'>
                        <Button type='button' variant='outline' onClick={handleCancel} disabled={saving}>
                            キャンセル
                        </Button>
                        <Button type='button' variant='active' onClick={handleSave} disabled={saving}>
                            {saving ? '保存中…' : '保存'}
                        </Button>
                    </div>
                </div>
            )}
        </SectionBox>
    );
});
